import React, { MouseEvent, useCallback, useMemo, useState } from 'react';
import { isNull } from 'lodash/fp';
import classnames from 'classnames';

import DatasetsMenu from '../../../assets/risk-tolerance/DatasetsMenu.png';
import ExcludeField from '../../../assets/risk-tolerance/ExcludeField.png';
import FallbackRiskPosition from '../../../assets/risk-tolerance/FallbackRiskPosition.png';
import RiskAssociated from '../../../assets/risk-tolerance/RiskAssociated.png';
import Weighting from '../../../assets/risk-tolerance/Weighting.png';
import { ConfirmationModal } from '../../shared/modal/ConfirmationModal';
import { Document, MultiAnalytics } from '../icons';
import { ModalHeader } from '../modal/ModalHeader';
import { Position, PositionModal } from '../modal/PositionModal';
import styles from './RiskTolerance.module.scss';
import { Scrollable } from '../scrollable/Scrollable';

enum RiskHelpElement {
    DATASETS = 'Datasets Menu',
    RISK_ASSOCIATED = 'Risk Associated Configuration',
    WEIGHTING = 'Level of Importance',
    FALLBACK_RISK_POSITION = 'Default Risk Position',
    EXCLUDE_RISK = 'Exclude Risk Field from my Calculations'
}

interface GuidanceModalProps {
    selectedRiskHelpElement: RiskHelpElement | null;
    position: Position | null;
    closeModal: () => void;
}

const GuidanceModal: React.FC<GuidanceModalProps> = ({ selectedRiskHelpElement, closeModal, position }) => {
    const guidanceHelp = useMemo(() => {
        switch (selectedRiskHelpElement) {
            case RiskHelpElement.DATASETS:
                return [
                    'This is a list of all of the available fields which a risk tolerance can be set against.',
                    'You will find that the list follows the structure of the data map associated with this agreement type',
                    'Each field will display the type of field beside the title of the field',
                    'Navigating through this menu will allow you to view and amend any of the risk configuration, level of importance, default position or whether to include this risk in your calculations'
                ];
            case RiskHelpElement.RISK_ASSOCIATED:
                return [
                    'This is the most important section of this page, allowing you to set which values you would associate with the varying levels of risk available',
                    'Risk levels vary from "Highly Desirable" to "Of Serious Concern", and for each of the fields available to configure you can decide what values you believe should fall into the various risk levels',
                    'The configuration element of this will vary slightly depending on the type of field you are configuring the risk against',
                    'Dropdown risk fields will only allow you to select from the options available within the data map dropdown. If you believe something is missing then you should contact Ark51 to discuss',
                    'Number risk fields will allow you configure tolerance levels for each risk using a combination of numbers and comparators such as <, >, = etc',
                    'Date risk fields will allow you configure tolerance levels for each risk using a combination of dates and comparators such as <, >, = etc',
                    'Currency risk fields will allow you configure tolerance levels for independent currencies using a combination of the currency and a number configuration as detailed above',
                    'Checkbox/Toggle risk fields will allow you to configure a risk level for the selected and unselected outcome of the toggle',
                    'Calculator risk fields will allow you to provide examples for each risk level, giving you the opportunity to set your gold standard and highlight anything in particular that falls outside of that',
                    'Text risk fields will allow you to provide examples for each risk level, giving you the opportunity to specify exact text matches that should assert a document to fall into each risk category'
                ];
            case RiskHelpElement.WEIGHTING:
                return [
                    'This slider will allow you to set the level of importance for this risk field',
                    'The higher the level of importance the more influence this will have on the overall risk score associated with a document',
                    'This should be used to highlight fields which are of significant importance when analysing your portfolio and will assign a higher weighting to the calculation'
                ];
            case RiskHelpElement.FALLBACK_RISK_POSITION:
                return [
                    'This slider allows you to set your fallback risk position, the default risk you would like to associate with this field if it did not meet the criteria of any of the risk categories',
                    'If you would like anything outside of the designated risk to be flagged to you instantly as a red flag then you can set this default position to be "Of Serious Concern" allowing immediate visibility to any outlying values within your portfolio',
                    'If you are happy to leave the default position as acceptable when it does not meet any of the risk tolerance criteria then this toggle can be left as it is without any alterations'
                ];
            case RiskHelpElement.EXCLUDE_RISK:
                return [
                    'This allows you to assert that you are not interested in this field for the calculations of your document risk score',
                    'If toggled, Ark51 will omit this field from all calculations so you can get a better understanding of the risk level of your portfolio without this field being considered at all'
                ];
            default:
                return [];
        }
    }, [selectedRiskHelpElement]);

    if (isNull(position) || isNull(selectedRiskHelpElement)) {
        return null;
    }

    return (
        <PositionModal
            isOpen
            closeModal={closeModal}
            position={position}
            testId={`risk-tolerance-help-modal-${selectedRiskHelpElement}`}
            height='fit-content'
            width='500px'
        >
            <div className={styles.guidanceHelpModal}>
                <ModalHeader label={selectedRiskHelpElement} icon={Document} />
                <div className={styles.guidanceHelpWrapper}>
                    <Scrollable maxHeight='40vh'>
                        {guidanceHelp.map((help, index) => (
                            <div key={index} className={styles.guidanceHelpItem}>
                                <div className={styles.guidanceHelpPoint} />
                                <div className={styles.guidanceHelpContent}>{help}</div>
                            </div>
                        ))}
                    </Scrollable>
                </div>
            </div>
        </PositionModal>
    );
};

interface HelpModalProps {
    isOpen: boolean;
    closeModal: () => void;
    isClientRiskView?: boolean;
}

export const HelpModal: React.FC<HelpModalProps> = ({ isOpen, closeModal, isClientRiskView = false }) => {
    const [selectedRiskHelpElement, setSelectedRiskHelpElement] = useState<RiskHelpElement | null>(null);
    const [guidancePosition, setGuidancePosition] = useState<Position | null>(null);

    const openHelpGuidanceModal = useCallback((e: MouseEvent<HTMLDivElement>, riskHelpElement: RiskHelpElement) => {
        setGuidancePosition({ x: e.clientX, y: e.clientY });
        setSelectedRiskHelpElement(riskHelpElement);
    }, []);

    const closeGuidanceModal = useCallback(() => {
        setGuidancePosition(null);
        setSelectedRiskHelpElement(null);
    }, []);

    const closeHelpModal = useCallback(() => {
        closeGuidanceModal();
        closeModal();
    }, [closeGuidanceModal, closeModal]);

    return (
        <ConfirmationModal
            isOpen={isOpen}
            closeModal={closeHelpModal}
            closeLabel='Close Help'
            showConfirm={false}
        >
            <div className={styles.helpWrapper}>
                <ModalHeader label='Risk Tolerance Guide' icon={MultiAnalytics} />
                <div className={styles.helpTitle}>Click on an element of the Risk Tolerance Screen below to get help</div>
                <div className={styles.helpContent}>
                    <div className={classnames(styles.datasetsMenuWrapper, { [styles.selectedHelpWrapper]: selectedRiskHelpElement === RiskHelpElement.DATASETS })} onClick={e => openHelpGuidanceModal(e, RiskHelpElement.DATASETS)}><img className={styles.imageWrapper} src={DatasetsMenu} /></div>
                    <div className={styles.riskWrapper}>
                        <div className={classnames(styles.riskAssociatedWrapper, { [styles.selectedHelpWrapper]: selectedRiskHelpElement === RiskHelpElement.RISK_ASSOCIATED })} onClick={e => openHelpGuidanceModal(e, RiskHelpElement.RISK_ASSOCIATED)}><img className={styles.imageWrapper} src={RiskAssociated} /></div>
                        <div className={styles.configWrapper}>
                            <div className={styles.slidersWrapper} style={{ height: `${isClientRiskView ? 80 : 100}%` }}>
                                <div className={classnames(styles.weightingSlider, { [styles.selectedHelpWrapper]: selectedRiskHelpElement === RiskHelpElement.WEIGHTING })} onClick={e => openHelpGuidanceModal(e, RiskHelpElement.WEIGHTING)}><img className={styles.imageWrapper} src={Weighting} /></div>
                                <div className={classnames(styles.fallbackSlider, { [styles.selectedHelpWrapper]: selectedRiskHelpElement === RiskHelpElement.FALLBACK_RISK_POSITION })} onClick={e => openHelpGuidanceModal(e, RiskHelpElement.FALLBACK_RISK_POSITION)}><img className={styles.imageWrapper} src={FallbackRiskPosition} /></div>
                            </div>
                            {isClientRiskView && <div className={classnames(styles.excludeFieldWrapper, { [styles.selectedHelpWrapper]: selectedRiskHelpElement === RiskHelpElement.EXCLUDE_RISK })} onClick={e => openHelpGuidanceModal(e, RiskHelpElement.EXCLUDE_RISK)}><img className={styles.imageWrapper} src={ExcludeField} /></div>}
                        </div>
                    </div>
                </div>
            </div>
            <GuidanceModal closeModal={closeGuidanceModal} selectedRiskHelpElement={selectedRiskHelpElement} position={guidancePosition} />
        </ ConfirmationModal>
    );
};
